import { getFunnelConfig } from 'components/Funnelling/api';
import { useCallback, useReducer } from 'react';
import type { APIFunnelJourney, JourneyType } from 'lib/sdks/strapi';
import { JOURNEY_TYPES } from 'lib/sdks/strapi/constants';
import { useDispatch, useSelector } from 'react-redux';
import { selectRecentSearchLocation } from 'containers/AppContainer/selectors';
import type { FunnelState, JourneyConfig, UseFunnelNext } from '../types';
import { getJourneyVariant } from '../utils/getJourneyVariant';
import type { Dispatch } from 'redux';

export enum ActionTypes {
  START = 'START',
  NEXT_SCREEN = 'NEXT_SCREEN',
  PREVIOUS_SCREEN = 'PREVIOUS_SCREEN',
  RESET = 'RESET',
  BACK_TO_START = 'BACK_TO_START',
}

type FunnelAction = {
  type: ActionTypes;
  payload: any;
};

type InitialiseJourneyOptions = {
  type: JourneyType;
  location?: string;
  journey?: APIFunnelJourney;
  startingScreen?: number;
};

type InitialiseJourneyReturnValue = Promise<
  | [journeyWithScreen: true, journeyConfig: JourneyConfig]
  | [journeyWithScreen: false, journeyConfig: JourneyConfig | null]
>;

type InitialiseJourney = (
  categoryId: number,
  options: InitialiseJourneyOptions,
) => InitialiseJourneyReturnValue;

const initialState = {
  categoryId: null,
  lastCategoryId: null,
  funnelConfig: null,
  steps: [],
  fieldValues: {},
  showLocation: false,
};

const actions: {
  [key in ActionTypes]: (
    state: FunnelState,
    action: FunnelAction,
  ) => FunnelState;
} = {
  [ActionTypes.START]: (state, action) => {
    const { categoryId, funnelConfig, startingScreen } = action.payload;

    return {
      ...initialState,
      steps:
        startingScreen === undefined
          ? [funnelConfig.startingScreen]
          : [startingScreen],
      categoryId,
      lastCategoryId: categoryId,
      funnelConfig,
    };
  },
  [ActionTypes.NEXT_SCREEN]: (state, action) => {
    const { currentScreen, nextScreen, value, categoryId } = action.payload;
    return {
      ...state,
      steps: !nextScreen ? state.steps : [...state.steps, nextScreen],
      fieldValues: { ...state.fieldValues, [currentScreen]: value },
      showLocation:
        !nextScreen && state.funnelConfig?.type !== JOURNEY_TYPES.enrichment,
      lastCategoryId: categoryId ? categoryId : state.lastCategoryId,
    };
  },
  [ActionTypes.PREVIOUS_SCREEN]: state => {
    const { steps } = state;

    const previousScreen = state.showLocation
      ? steps[steps.length - 1]
      : steps[steps.length - 2];

    return {
      ...state,
      fieldValues: Object.fromEntries(
        Object.entries(state.fieldValues).filter(
          ([key]) => key !== previousScreen.toString(),
        ),
      ),
      steps: state.showLocation ? steps : steps.slice(0, -1),
      showLocation: false,
    };
  },
  [ActionTypes.BACK_TO_START]: state => {
    return {
      ...state,
      steps: state.steps.slice(0, 1),
      showLocation: false,
    };
  },
  [ActionTypes.RESET]: () => initialState,
};

export const funnelReducer = (state: FunnelState, action: FunnelAction) => {
  if (actions[action.type]) {
    return actions[action.type](state, action);
  }

  return state;
};

export const findNextPath = (
  funnelConfig: APIFunnelJourney,
  currentScreen: number,
  value: string,
) => {
  const { paths } = funnelConfig;

  const matchingPath = paths.find(
    path => path.currentScreen === currentScreen && path.option === value,
  );

  return (
    matchingPath ||
    paths.find(path => path.currentScreen === currentScreen && !path.option)
  );
};

const getJourney = async ({
  categoryId,
  type,
  storeDispatch,
}: {
  categoryId: number;
  type: JourneyType;
  storeDispatch: Dispatch<any>;
}) => {
  const journey = await getFunnelConfig(categoryId, type);

  if (!journey) return null;

  return getJourneyVariant({ journey, storeDispatch });
};

const useFunnelling = () => {
  const [
    {
      categoryId,
      lastCategoryId,
      funnelConfig,
      steps,
      fieldValues,
      showLocation,
    },
    dispatch,
  ] = useReducer(funnelReducer, initialState);

  const reduxDispatch = useDispatch();
  const initialLocation = useSelector(selectRecentSearchLocation);

  const initialise = useCallback<InitialiseJourney>(
    async (initialiseCategoryId, options) => {
      const { type, location, journey, startingScreen } = options;

      let config = journey || null;

      if (!journey) {
        config = await getJourney({
          categoryId: initialiseCategoryId,
          type,
          storeDispatch: reduxDispatch,
        });
      }

      if (!config) {
        return [false, null];
      }

      if (!config.startingScreen) {
        return [false, config as JourneyConfig];
      }

      dispatch({
        type: ActionTypes.START,
        payload: {
          categoryId: initialiseCategoryId,
          funnelConfig: { ...config, location },
          startingScreen,
        },
      });
      return [true, config as JourneyConfig];
    },
    [reduxDispatch],
  );

  const stop = () => {
    dispatch({ type: ActionTypes.RESET, payload: {} });
  };

  const next = useCallback<UseFunnelNext>(
    ({ value, categoryId: selectedCategoryId }, callback) => {
      if (!funnelConfig) {
        return;
      }

      const currentScreen = steps[steps.length - 1];

      const nextPath = findNextPath(funnelConfig, currentScreen, value);

      dispatch({
        type: ActionTypes.NEXT_SCREEN,
        payload: {
          currentScreen,
          nextScreen: nextPath?.goTo,
          value,
          categoryId: selectedCategoryId,
        },
      });
      if (callback) {
        callback(currentScreen, value, nextPath?.goTo);
      }
    },
    [funnelConfig, steps],
  );

  const previous = useCallback(() => {
    dispatch({ type: ActionTypes.PREVIOUS_SCREEN, payload: {} });
  }, []);

  const backToStart = useCallback(() => {
    dispatch({ type: ActionTypes.BACK_TO_START, payload: {} });
  }, []);

  return {
    initialise,
    stop,
    next,
    previous,
    backToStart,
    categoryId,
    showLocation,
    steps,
    lastCategoryId,
    fieldValues,
    funnelConfig,
    initialLocation,
  };
};

export type UseFunnellingReturnType = ReturnType<typeof useFunnelling>;

export default useFunnelling;
